import { createContext, useContext } from 'react';

// The dashboard's own toast channel (GRAVY-10443).
//
// Kept apart from the app-wide `@/components/Toast`, which is a single-message
// snackbar with no tone or title; the Take Over toasts are a titled, toned stack
// drawn in the dashboard's design language.
//
// The context lives in its own module, not beside the provider, because the
// provider renders the Shift Detail panel and the panel raises toasts — one
// file would make that an import cycle.

export type DashboardToastTone = 'info' | 'warning' | 'critical';

export type DashboardToast = {
  /** Stable identity; a toast raised twice with one key shows once. */
  key: string;
  tone: DashboardToastTone;
  title: string;
  message: string;
  /** When set, clicking the toast opens this shift's detail panel. */
  shiftId?: number;
};

export type DashboardToastContextShape = {
  notify: (toast: DashboardToast) => void;
};

// A no-op default, so a component rendered outside the provider (the panel in
// its unit tests, say) raises nothing rather than throwing.
export const DashboardToastContext = createContext<DashboardToastContextShape>({
  notify: () => undefined,
});

const useDashboardToast = () => useContext(DashboardToastContext);

export default useDashboardToast;
